import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { moduleService } from "@/services/module.service";
import { governanceService } from "@/services/governance.service";
import toast from "react-hot-toast";

/**
 * Loads database-owned entitlement and permission data. Page routes and
 * navigation are intentionally excluded: PAGE_POLICIES is their only runtime
 * registry.
 */
export function useModuleCoverage() {
  const queryClient = useQueryClient();

  const { data: modules = [] } = useQuery({
    queryKey: ["admin-modules"],
    queryFn: moduleService.getAllModules,
  });

  const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ["admin-permissions"],
    queryFn: governanceService.listPermissions,
  });

  const moduleMap = useMemo(
    () => new Map(modules.map((module) => [module.code, module])),
    [modules],
  );

  const { mutateAsync: updatePermission, isPending: isUpdating } = useMutation({
    mutationFn: (payload: { id: string; moduleCode: string }) =>
      governanceService.updatePermission(payload.id, {
        moduleCode: payload.moduleCode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-permissions"] });
      toast.success("Permission updated");
    },
    onError: (error: any) =>
      toast.error(error?.message || "Failed to update permission"),
  });

  return {
    modules,
    permissions,
    permissionsLoading,
    moduleMap,
    updatePermission,
    isUpdating,
  };
}
